import type { StateCreator } from 'zustand';
import type {
  ActionTrailStep,
  BlockOutput,
  CandidateSummary,
  MessageAttachment,
  SuggestedAction,
  TranscriptMessage,
} from '../../types';

export type BarMode = 'docked' | 'floating' | 'fullscreen' | 'minimized';

export interface OpenTab {
  sessionId: string;
  title: string;
  status: 'idle' | 'streaming' | 'done' | 'error';
  createdAt: string;
}

export interface ServerMessageInput {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  /** Uploaded-file attachments on a user turn (download chips). */
  attachments?: MessageAttachment[];
  /** Pre-assembled output blocks for assistant turns. The api adapter folds
   * tool-message metadata (structured_data) into the following assistant
   * turn so the rich UI survives a page refresh. Empty for user turns. */
  outputs?: BlockOutput[];
  /** Completed-step trail rebuilt from the transcript's tool messages so the
   * "what the agent did" footer survives hydration/refresh instead of
   * vanishing when the server snapshot replaces the local state. */
  actionTrail?: ActionTrailStep[];
}

// One row inside the "Thinking…" card — a tool call or pipeline step.
export interface StreamingSubStep {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'done' | 'error';
  durationMs?: number;
}

// The assistant's reply while it's still being written. `content` grows
// with each stream_chunk; `subSteps` fills as tools start and finish.
// `outputs` collects rich blocks that tool_result events produce
// (candidate lists, action results, etc.) — they survive into the final
// committed message alongside the streamed text. Cleared at run end.
export interface StreamingDraft {
  sessionId: string;
  content: string;
  subSteps: StreamingSubStep[];
  outputs: BlockOutput[];
  /** Position in the session's transcript where the eventual assistant
   * message should land. Captured when the draft is first opened so that a
   * user-initiated message during streaming (e.g. a chip click) inserts
   * AFTER the assistant's reply rather than before it. */
  insertAt: number;
  /** Epoch ms of the last event that touched this draft. A draft that goes
   * silent past the stale window is a zombie (straggler event after
   * stream_done, killed worker) — the stream hook's watchdog commits and
   * clears it so it can't wedge the send gate or block hydration forever. */
  updatedAt?: number;
}

/** Turn-ownership bookkeeping for one session's event stream. */
export interface TurnScope {
  activeTurn: string | null;
  lastDoneTurn: string | null;
  lastAcceptedAt: number;
}

export interface AgentForgeState {
  openTabs: OpenTab[];
  activeSessionId: string | null;
  mode: BarMode;
  historyOpen: boolean;
  // Linear-style "New chat" state: panel renders an empty composer without
  // committing a tab. The tab + session is created on first send.
  draftOpen: boolean;
  messagesBySession: Record<string, TranscriptMessage[]>;
  // The reply currently being streamed in. `null` when no run is active.
  streamingDraft: StreamingDraft | null;
  // Live WebSocket health, stamped by the AgentForge umbrella (the single
  // socket owner). Surfaces the "Reconnecting…" pill — a mid-turn socket
  // drop must never read as a frozen chat.
  wsReconnecting: boolean;
  // Unsent composer text per session (serialized form — mention markers
  // survive). Persisted: switching sessions or reloading must not eat a
  // half-typed message.
  draftBySession: Record<string, string>;
  // One queued message per session: typed while a turn was still running,
  // auto-sent when that turn finishes and the session is active. NOT
  // persisted — a queued send firing days later on reload would be worse
  // than losing it.
  queuedBySession: Record<string, string>;
  // 0-100 while a multipart send's attachments are uploading; null otherwise.
  uploadProgress: number | null;
  // Per-block row selection, keyed by the originating tool_call_id of the
  // card. Empty / missing entry means "no explicit selection" — chip
  // actions fall back to acting on all visible rows. The UI is the source
  // of truth for user intent; chip prompt templates carry placeholders
  // ({ids}, {count}, {first_id}) that the FE substitutes at submit time.
  selectionByBlock: Record<string, string[]>;
  // Which turn (RunAgent workflow id) currently owns each session's
  // streaming draft, which turn last committed, and when the owner last
  // spoke. Lives in the store — NOT a hook closure — so an HMR remount or
  // effect re-run mid-turn doesn't forget the owner and misattribute
  // events. Not persisted: a page reload legitimately starts with no live
  // turn.
  turnScopeBySession: Record<string, TurnScope>;
  // Path the floating overlay bridged into when the user clicked a result
  // (candidate / table row) from the inline agent view. The widget stays open
  // on that page so the chat context survives the jump, but auto-minimizes
  // once the user navigates away. `null` for deliberately-opened floating
  // (bar / keyboard), which is meant to follow the user across routes.
  floatingBridgePath: string | null;
  // Candidate pinned in the right-docked profile panel; null when closed.
  profileCandidate: CandidateSummary | null;
}

export interface TabsActions {
  openTab: (_tab: OpenTab) => void;
  closeTab: (_sessionId: string) => void;
  setActive: (_sessionId: string | null) => void;
  setTabStatus: (_sessionId: string, _status: OpenTab['status']) => void;
  setTabTitle: (_sessionId: string, _title: string) => void;
  // Re-keys everything held under a temporary local session id (the
  // instant-feedback placeholder shown while create-session is in flight)
  // onto the real server id.
  migrateSession: (_fromId: string, _toId: string) => void;
}

export interface UiActions {
  setMode: (_mode: BarMode) => void;
  // Open the floating overlay as a context bridge to `path` (a result page the
  // user clicked into). Records the path so a later navigation auto-closes it.
  openFloatingBridge: (_path: string) => void;
  toggleHistory: (_open?: boolean) => void;
  openDraft: () => void;
  closeDraft: () => void;
  reset: () => void;
  setWsReconnecting: (_reconnecting: boolean) => void;
  setUploadProgress: (_progress: number | null) => void;
  // Pin / dismiss a candidate's profile in the right-docked panel.
  openProfile: (_candidate: CandidateSummary) => void;
  closeProfile: () => void;
}

export interface MessagesActions {
  appendUserMessage: (_sessionId: string, _content: string) => void;
  // Replaces the entire transcript with what the server reports. Used by
  // the polling hook to mirror `GET /sessions/{id}/messages` into the store.
  hydrateMessages: (
    _sessionId: string,
    _serverMessages: ServerMessageInput[]
  ) => void;
  setSessionDraft: (_sessionId: string, _text: string) => void;
  setQueuedMessage: (_sessionId: string, _text: string) => void;
  clearQueuedMessage: (_sessionId: string) => void;
}

export interface StreamingActions {
  // WS streaming-draft actions. Each takes a sessionId so frames meant for
  // another (older) session are silently ignored — see the implementations.
  appendStreamDelta: (_sessionId: string, _delta: string) => void;
  // Clears the draft's accumulated TEXT (keeps tool sub-steps/outputs) so a
  // re-streamed LLM attempt doesn't duplicate the already-shown prefix.
  resetStreamDraft: (_sessionId: string) => void;
  pushSubStep: (_sessionId: string, _step: StreamingSubStep) => void;
  patchSubStep: (
    _sessionId: string,
    _id: string,
    _patch: Partial<Omit<StreamingSubStep, 'id'>>
  ) => void;
  // Append a rich block (candidate list, action result, etc.) produced by
  // a tool_result. Block order matches the order tools complete.
  pushOutputBlock: (_sessionId: string, _block: BlockOutput) => void;
  // Patch the suggested-action chips on a block identified by tool_call_id.
  // Touches both the live streaming draft (current turn) and any committed
  // messages (prior turns or hydrated history) so smart chips replace
  // static defaults wherever the block lives.
  patchBlockChips: (
    _sessionId: string,
    _toolCallId: string,
    _chips: SuggestedAction[]
  ) => void;
  clearStreamingDraft: () => void;
  // Promote the streaming draft into a permanent assistant message on the
  // session's transcript and clear the draft. Used while the HTTP hydrate
  // is bypassed — the WS frames become the source of truth and we
  // self-commit at run-end so the streamed text doesn't keep growing on
  // the next turn.
  commitStreamingDraft: () => void;
  // Merge turn-ownership bookkeeping for a session (see turnScopeBySession).
  setTurnScope: (_sessionId: string, _patch: Partial<TurnScope>) => void;
}

export interface SelectionActions {
  // Per-card row selection. Toggle adds/removes a single id; setBlockSelection
  // replaces the full set (used by select-all / clear-all in the card header).
  toggleBlockSelection: (_toolCallId: string, _id: string) => void;
  setBlockSelection: (_toolCallId: string, _ids: string[]) => void;
  clearBlockSelection: (_toolCallId: string) => void;
}

export type AgentForgeActions = TabsActions &
  UiActions &
  MessagesActions &
  StreamingActions &
  SelectionActions;

export type AgentForgeStore = AgentForgeState & AgentForgeActions;

// Slices are composed under the persist middleware, so they share its mutator.
export type SliceCreator<T> = StateCreator<
  AgentForgeStore,
  [['zustand/persist', unknown]],
  [],
  T
>;

export const INITIAL_STATE: AgentForgeState = {
  openTabs: [],
  activeSessionId: null,
  mode: 'docked',
  historyOpen: false,
  draftOpen: false,
  messagesBySession: {},
  streamingDraft: null,
  wsReconnecting: false,
  draftBySession: {},
  queuedBySession: {},
  uploadProgress: null,
  selectionByBlock: {},
  turnScopeBySession: {},
  floatingBridgePath: null,
  profileCandidate: null,
};
